import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { shallow } from "zustand/shallow";

import useIntakeFormStore from "../../../store/IntakeForm.store";
import {
  trackCategorySelect,
  trackStrategySelect,
} from "../../../utils/helpers/analytics";
import { getStrategyDetailByName } from "../../../utils/helpers/intakeForm";
import Card from "../../Card";
import SelectInput from "../../Form/SelectInput";

const SelectStrategyCard = ({ id, headerIcon }) => {
  const [formOptions, formState, formData] = useIntakeFormStore(
    (state) => [
      state.data.formOptions,
      state.data.formState,
      state.data.formData,
    ],
    shallow
  );
  const { handleSetIntakeFormStates } = useIntakeFormStore.getState().actions;
  const [strategyOptions, setStrategyOptions] = useState([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const isLocked = formState.isFormLocked;

  const { control, setValue } = useFormContext();
  const categoryValue = useWatch({
    control,
    name: "selectCategory",
  });
  const strategyValue = useWatch({
    control,
    name: "selectStrategy",
  });

  const { isDirty } = useFormState();

  useEffect(() => {
    if (categoryValue && isDirty) trackCategorySelect(categoryValue);
    const strategy = formOptions?.strategies[categoryValue] || [];

    setStrategyOptions(strategy);

    handleSetIntakeFormStates({
      category: categoryValue || null,
    });
  }, [categoryValue, handleSetIntakeFormStates, formOptions, isDirty]);

  useEffect(() => {
    if (strategyOptions.length > 0) {
      const selectedOption =
        strategyOptions.find((opt) =>
          strategyValue
            ? opt.value === strategyValue
            : isFirstLoad && opt.label === formData?.strategyName
        ) || null;

      if (selectedOption?.label === formData?.strategyName) {
        setIsFirstLoad(false);
      }

      setValue("selectStrategy", selectedOption?.value);

      if (!selectedOption) {
        setValue("selectStrategy", "");
      }

      const strategyDetail = getStrategyDetailByName(
        formOptions.strategyDetails,
        selectedOption?.label
      );
      if (strategyDetail && isDirty)
        trackStrategySelect(strategyDetail.strategyName);

      handleSetIntakeFormStates({
        strategyCode: selectedOption?.value || null,
        strategyDetail: strategyDetail || null,
      });
    }
  }, [
    strategyOptions,
    strategyValue,
    formOptions,
    formData,
    handleSetIntakeFormStates,
    setValue,
    isDirty,
  ]);

  return (
    <Card
      id={id}
      title="Select Strategy"
      subtitle="Required Fields"
      headerIcon={headerIcon}
      openByDefault={true}
    >
      <div className="card__content">
        <SelectInput
          key="selectCategory"
          name="selectCategory"
          label="Asset style"
          options={{
            required: true,
            readOnly: isLocked,
            items: formOptions.categories || [],
          }}
        />
        <SelectInput
          key="selectStrategy"
          name="selectStrategy"
          label="Strategy"
          options={{
            required: true,
            disabled: typeof categoryValue === "undefined",
            readOnly: isLocked,
            items: strategyOptions,
          }}
        />
      </div>
    </Card>
  );
};

SelectStrategyCard.propTypes = {
  id: PropTypes.string,
  headerIcon: PropTypes.string,
};

export default SelectStrategyCard;
